import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut, UserCircle2, Settings, Shield, AlertCircle, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { SubscriptionPill } from "@/components/SubscriptionPill";
import { cn } from "@/lib/utils";

const STUDENT_NAV = [
    { to: "/", label: "Home" },
    { to: "/practice/setup", label: "Practice" },
    { to: "/history", label: "History" },
    { to: "/all-sessions", label: "All sessions" },
    { to: "/learn", label: "Learn" },
    { to: "/support", label: "Support" },
];

// Per Phase 14: the Admin link is appended (rightmost, before avatar) when role === "admin".
const ADMIN_NAV_ITEM = { to: "/admin", label: "Admin" };

const navLinkClasses = ({ isActive }) =>
    cn(
        "rounded-md px-3 py-2 text-sm font-medium transition-colors",
        isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
    );

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const isAdmin = user?.role === "admin";
    const NAV = isAdmin ? [...STUDENT_NAV, ADMIN_NAV_ITEM] : STUDENT_NAV;

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-background grain-bg flex flex-col">
            <ComplimentaryExpiryBanner user={user} />
            <header className="sticky top-0 z-30 border-b border-border/80 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
                    <div className="flex items-center gap-8">
                        <Link
                            to="/"
                            className="flex items-center gap-2"
                            data-testid="brand-link"
                        >
                            <img
                                src="/happylab-logo-mark.png"
                                alt="Happy Lab"
                                className="h-7 w-auto object-contain sm:h-8"
                                data-testid="brand-logo-mark"
                            />
                            <span className="hidden text-lg font-semibold tracking-tight text-foreground sm:inline">
                                Happy Lab
                            </span>
                        </Link>
                        <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
                            {NAV.map((item) => {
                                const unread = item.to === "/support" && (user?.unread_support_replies || 0) > 0;
                                return (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.to === "/"}
                                        className={({ isActive }) =>
                                            cn(
                                                navLinkClasses({ isActive }),
                                                item.to === "/admin" && "inline-flex items-center gap-1.5",
                                                unread && "relative"
                                            )
                                        }
                                        data-testid={`nav-link-${item.label.toLowerCase()}`}
                                    >
                                        {item.to === "/admin" && <Shield className="h-4 w-4" aria-hidden />}
                                        {item.label}
                                        {unread && (
                                            <span
                                                className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-rose-500"
                                                data-testid="nav-support-unread-dot"
                                                aria-label="Unread support reply"
                                            />
                                        )}
                                    </NavLink>
                                );
                            })}
                        </nav>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Subscription pill — Phase 27 */}
                        <SubscriptionPill user={user} className="hidden sm:inline-flex" />
                        {/* User dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    data-testid="user-menu-trigger"
                                    aria-label="Open user menu"
                                >
                                    <InitialsAvatar name={user?.full_name || "?"} />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="flex flex-col gap-0.5">
                                    <span className="text-sm font-semibold text-foreground">
                                        {user?.full_name}
                                    </span>
                                    <span className="text-xs font-normal text-muted-foreground">
                                        {user?.email}
                                    </span>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate("/profile")}
                                    data-testid="user-menu-profile"
                                >
                                    <UserCircle2 className="h-4 w-4" />
                                    Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate("/account")}
                                    data-testid="user-menu-account"
                                >
                                    <Settings className="h-4 w-4" />
                                    Account
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={handleLogout}
                                    data-testid="user-menu-logout"
                                    className="text-destructive focus:text-destructive"
                                >
                                    <LogOut className="h-4 w-4" />
                                    Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Mobile hamburger */}
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="md:hidden"
                                    data-testid="mobile-nav-trigger"
                                    aria-label="Open navigation"
                                >
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-72">
                                <SheetTitle className="sr-only">Navigation</SheetTitle>
                                <SheetDescription className="sr-only">
                                    Site navigation links
                                </SheetDescription>
                                <div className="mt-8 flex flex-col gap-1" data-testid="mobile-nav">
                                    {NAV.map((item) => (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            end={item.to === "/"}
                                            onClick={() => setMobileOpen(false)}
                                            className={({ isActive }) =>
                                                cn(
                                                    "rounded-md px-3 py-2 text-base font-medium",
                                                    isActive
                                                        ? "bg-primary/10 text-primary"
                                                        : "text-foreground hover:bg-secondary"
                                                )
                                            }
                                            data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                                        >
                                            {item.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10">{children}</div>
            </main>

            <footer className="border-t border-border/60 bg-background/60">
                <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 text-xs text-muted-foreground">
                    Happy Lab — practice safely. Built for counselling students.
                </div>
            </footer>
        </div>
    );
};

// ---------------------------------------------------------------
// Phase 19 — Global expiry warning banner.
// Shows when user is on complimentary plan with <=7 days remaining.
// Dismissible per-session via sessionStorage.
// ---------------------------------------------------------------
const ComplimentaryExpiryBanner = ({ user }) => {
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        if (sessionStorage.getItem("hl_comp_banner_dismissed") === "1") {
            setDismissed(true);
        }
    }, []);

    const sub = user?.subscription || {};
    const daysLeft = user?.expires_in_days;
    const show =
        !dismissed &&
        sub.plan === "complimentary" &&
        sub.status === "active" &&
        typeof daysLeft === "number" &&
        daysLeft <= 7;

    if (!show) return null;

    const dismiss = () => {
        sessionStorage.setItem("hl_comp_banner_dismissed", "1");
        setDismissed(true);
    };

    return (
        <div
            data-testid="complimentary-expiry-banner"
            className="border-b border-amber-200 bg-amber-50 text-amber-900"
        >
            <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5 text-sm sm:px-6">
                <AlertCircle className="h-4 w-4 flex-none" />
                <p className="flex-1">
                    Your complimentary access ends in <span className="font-semibold">{daysLeft} day{daysLeft === 1 ? "" : "s"}</span>.{" "}
                    <Link to="/subscribe" className="font-medium underline-offset-2 hover:underline">
                        Subscribe now to avoid interruption →
                    </Link>
                </p>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className="rounded-md p-1 text-amber-800 hover:bg-amber-100"
                    data-testid="complimentary-expiry-banner-dismiss"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

